/**
 * Media conversions behind the checker's file fixes (W2-12a checker-runtime; DESIGN.md §7.6 PH-01, PH-02, PH-04,
 * PH-27, ST-07, ST-08; formats-and-packaging.md P§2.7): convert a song or hit sound to WAV, an image Phira cannot
 * decode to PNG, re-time a GIF whose frames add up to 0 ms, and downscale a large line image. Each conversion
 * reads the stored bytes (persist), produces the new file, stores it (content-addressed) and returns the AssetRef
 * to add; the fix command then re-points every reference in ONE transaction (fixes.ts).
 *
 *  - Audio: decoded in the browser (Web Audio, at the file's own sample rate when known), encoded as 16-bit WAV in
 *    the audio worker (`encodeWav`, core/audio-dsp/wav.ts), or on this thread when the worker is unavailable.
 *  - Images: decoded and re-encoded by the io worker (`downscaleImage`, PNG or the original JPEG), facts from
 *    `imageMeta` and `sniffMedia`.
 *  - GIF delays: rewritten byte for byte here (gif.ts), facts updated to what Phira will read.
 *
 * Every failure becomes a `MediaFixError` with a `kind` (reading the stored file, decoding it, storing the result,
 * ...); fixes.ts turns the kind into a localised toast and keeps the technical text (`message`, `cause`) for the
 * console only.
 */

import type { AppServices } from '../../app/registry/types';
import { encodeWav } from '../../core/audio-dsp/wav';
import type { AssetMeta, AssetRef } from '../../core/model/types';
import { GifFormatError, setGifFrameDelays } from './gif';

export type MediaServices = Pick<AppServices, 'persist' | 'io' | 'dsp'>;

/** What went wrong in a media fix (each kind has its own message; the technical text goes to the console). */
export type MediaFailure =
  /** The stored file could not be read. */
  | 'read'
  /** The browser has no audio decoder (no Web Audio). */
  | 'noDecoder'
  /** The browser could not decode the song or sound. */
  | 'decodeAudio'
  /** The file decodes to no audio channels. */
  | 'noAudio'
  /** The image could not be decoded or encoded. */
  | 'image'
  /** The GIF is not well formed. */
  | 'gif'
  /** The converted file could not be stored (storage full or unavailable). */
  | 'store';

export class MediaFixError extends Error {
  readonly kind: MediaFailure;
  constructor(kind: MediaFailure, message: string, options?: { readonly cause?: unknown }) {
    super(message, options);
    this.name = 'MediaFixError';
    this.kind = kind;
  }
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Runs one step of a conversion; a failure becomes a MediaFixError of `kind` (an earlier one is kept). */
async function step<T>(kind: MediaFailure, what: string, run: () => Promise<T> | T): Promise<T> {
  try {
    return await run();
  } catch (e) {
    if (e instanceof MediaFixError) throw e;
    throw new MediaFixError(kind, `${what}: ${describe(e)}`, { cause: e });
  }
}

function readAsset(s: MediaServices, from: AssetRef): Promise<Uint8Array> {
  return step('read', `reading ${from.path}`, () => s.persist.getAsset(from.id));
}

/** `path` with its extension replaced (or added). */
export function withExtension(path: string, ext: string): string {
  const slash = path.lastIndexOf('/');
  const dot = path.lastIndexOf('.');
  const base = dot > slash ? path.slice(0, dot) : path;
  return `${base}.${ext}`;
}

/** A copy of the bytes in their own ArrayBuffer (decoders detach or keep what they are given). */
function ownBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  return copy.buffer;
}

interface DecodedAudio {
  readonly channels: Float32Array[];
  readonly sampleRate: number;
  readonly durationSec: number;
}

const DEFAULT_SAMPLE_RATE = 44100;

async function decodeAudio(bytes: Uint8Array, sampleRate: number): Promise<DecodedAudio> {
  const Ctor = (globalThis as { OfflineAudioContext?: typeof OfflineAudioContext }).OfflineAudioContext;
  if (Ctor === undefined) throw new MediaFixError('noDecoder', 'OfflineAudioContext is not available');
  const buffer = await step('decodeAudio', 'decoding audio', () =>
    new Ctor(1, 1, sampleRate).decodeAudioData(ownBuffer(bytes)),
  );
  const channels: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) channels.push(Float32Array.from(buffer.getChannelData(c)));
  return { channels, sampleRate: buffer.sampleRate, durationSec: buffer.duration };
}

async function wavBytes(s: MediaServices, audio: DecodedAudio): Promise<Uint8Array> {
  try {
    return await s.dsp.call('encodeWav', { channels: audio.channels, sampleRate: audio.sampleRate });
  } catch {
    // The audio worker is optional: the encoder is pure and also runs here.
    return encodeWav(audio.channels, audio.sampleRate, 16);
  }
}

async function storedAsset(
  s: MediaServices,
  bytes: Uint8Array,
  ref: Omit<AssetRef, 'id' | 'bytes'>,
): Promise<AssetRef> {
  const meta = ref.meta ?? {};
  const id = await step('store', `storing ${ref.path}`, () => s.persist.putAsset(bytes, ref.mime, ref.role, meta));
  return { ...ref, id, bytes: bytes.length, meta };
}

/** Decodes a song or hit sound and stores it as 16-bit WAV. */
export async function convertAudioToWav(s: MediaServices, from: AssetRef): Promise<AssetRef> {
  const src = await readAsset(s, from);
  const audio = await decodeAudio(src, from.meta?.sampleRate ?? DEFAULT_SAMPLE_RATE);
  if (audio.channels.length === 0) throw new MediaFixError('noAudio', `${from.path} decodes to no channels`);
  const wav = await wavBytes(s, audio);
  const meta: AssetMeta = {
    codec: 'wav',
    durationSec: audio.durationSec,
    sampleRate: audio.sampleRate,
    channels: audio.channels.length,
    phiraDecodable: true,
  };
  return storedAsset(s, wav, { path: withExtension(from.path, 'wav'), mime: 'audio/wav', role: from.role, meta });
}

async function imageFacts(s: MediaServices, bytes: Uint8Array, name: string, base: AssetMeta): Promise<AssetMeta> {
  const [info, sniff] = await step('image', `reading the facts of ${name}`, () =>
    Promise.all([s.io.call('imageMeta', { bytes }), s.io.call('sniffMedia', { bytes, name })]),
  );
  const meta: AssetMeta = {
    ...base,
    width: info.width,
    height: info.height,
    codec: sniff.codec,
    phiraDecodable: sniff.phiraDecodable,
  };
  return meta;
}

/** Largest image side the decoder accepts for a format conversion (no scaling intended). */
const NO_SCALE = 16384;

/** Re-encodes an image Phira cannot decode (AVIF, PPM, ...) as PNG. */
export async function convertImageToPng(s: MediaServices, from: AssetRef): Promise<AssetRef> {
  const src = await readAsset(s, from);
  const png = await step('image', `converting ${from.path} to PNG`, () =>
    s.io.call('downscaleImage', { bytes: src, maxSide: NO_SCALE, mime: 'image/png' }),
  );
  const path = withExtension(from.path, 'png');
  const meta = await imageFacts(s, png, path, {});
  return storedAsset(s, png, { path, mime: 'image/png', role: from.role, meta });
}

/** Downscales an image so its longer side is at most `maxSide` px (PNG, or JPEG for JPEG files). */
export async function downscaleImage(s: MediaServices, from: AssetRef, maxSide: number): Promise<AssetRef> {
  const src = await readAsset(s, from);
  const jpeg = from.mime === 'image/jpeg' || from.meta?.codec === 'jpg' || from.meta?.codec === 'jpeg';
  const mime = jpeg ? 'image/jpeg' : 'image/png';
  const out = await step('image', `downscaling ${from.path}`, () =>
    s.io.call('downscaleImage', { bytes: src, maxSide, mime }),
  );
  const path = jpeg ? from.path : withExtension(from.path, 'png');
  const meta = await imageFacts(s, out, path, {});
  return storedAsset(s, out, { path, mime, role: from.role, meta });
}

/** Rewrites every frame delay of a GIF to `frameMs` (Phira hangs on a GIF whose delays add up to 0). */
export async function reencodeGif(s: MediaServices, from: AssetRef, frameMs: number): Promise<AssetRef> {
  const src = await readAsset(s, from);
  let r: ReturnType<typeof setGifFrameDelays>;
  try {
    r = setGifFrameDelays(src, frameMs);
  } catch (e) {
    if (!(e instanceof GifFormatError)) throw e;
    throw new MediaFixError('gif', `${from.path}: ${e.message}`, { cause: e });
  }
  const delays = Array.from({ length: r.frames }, () => r.delayMs);
  const meta: AssetMeta = {
    ...(from.meta ?? {}),
    codec: 'gif',
    gifFrames: r.frames,
    gifDelaysMs: delays,
    gifTotalMs: r.frames * r.delayMs,
    phiraDecodable: true,
  };
  return storedAsset(s, r.bytes, { path: from.path, mime: 'image/gif', role: from.role, meta });
}
